"use client";

import { c as _c } from "react/compiler-runtime";
import type { ToastApi, ToastOptions, ToastRecord, ToastShorthand, ToastVariant } from "@/types/toast";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { createContext, useContext, useMemo, useState } from "react";
import { ToastViewport } from "@/ui/toast/viewport";
export interface ToastContextValue {
  toast: ToastApi;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}
const DEFAULT_DURATION = 5000;
let sequence = 0;
const ToastContext = createContext<ToastContextValue | null>(null);
export interface ToastProviderProps {
  children: ReactNode;
  maxVisible?: number;
}
function createToastContextValue(setToasts: Dispatch<SetStateAction<ToastRecord[]>>) {
  const toast = (options: ToastOptions) => {
    const id = options.id ?? `toast-${++sequence}`;
    setToasts(current => {
      const existing = current.find(toast => toast.id === id);
      const next = {
        id,
        title: options.title,
        description: options.description,
        variant: options.variant ?? "default",
        duration: options.duration ?? DEFAULT_DURATION,
        action: options.action,
        revision: existing ? existing.revision + 1 : 0
      } satisfies ToastRecord;
      return existing ? current.map(toast => toast.id === id ? next : toast) : [...current, next];
    });
    return id;
  };
  const shorthand = (variant => (title, options) => toast({
    ...options,
    title,
    variant
  })) satisfies (variant: ToastVariant) => ToastShorthand;
  toast.success = shorthand("success");
  toast.error = shorthand("error");
  toast.info = shorthand("info");
  return {
    toast,
    dismiss: id => setToasts(current => current.filter(toast => toast.id !== id)),
    dismissAll: () => setToasts([])
  } satisfies ToastContextValue;
}
export function ToastProvider(t0) {
  const $ = _c(8);
  const {
    children,
    maxVisible: t1
  } = t0;
  const maxVisible = t1 === undefined ? 3 : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [toasts, setToasts] = useState(t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = createToastContextValue(setToasts);
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const value = t3;
  let t4;
  if ($[2] !== maxVisible || $[3] !== toasts) {
    t4 = <ToastViewport toasts={toasts} maxVisible={maxVisible} onDismiss={value.dismiss} />;
    $[2] = maxVisible;
    $[3] = toasts;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== children || $[6] !== t4) {
    t5 = <ToastContext value={value}>{children}{t4}</ToastContext>;
    $[5] = children;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within <ToastProvider>");
  }
  return context;
}